import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Lock, CheckCircle, ArrowLeft } from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import AxonallLogo from '../components/AxonallLogo'

const PasswordResetPage = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm()

  const password = watch('password')

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        toast.error('Invalid password reset link')
        navigate('/login')
        return
      }

      try {
        const response = await travelAgentService.verifyResetToken(token)
        setTokenValid(true)
        setUserEmail(response.data?.email || '')
      } catch (error) {
        toast.error(error.message || 'Invalid or expired reset link')
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      } finally {
        setIsVerifying(false)
      }
    }

    verifyToken()
  }, [token, navigate])

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const response = await travelAgentService.resetPassword(token, data.password)
      toast.success(response.message || 'Password reset successful')
      setResetSuccess(true)
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (error) {
      toast.error(error.message || 'Failed to reset password')
    } finally {
      setIsLoading(false)
    }
  }

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Verifying reset link...</p>
        </div>
      </div>
    )
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 py-8 px-4">
        <div className="max-w-md w-full">
          <div className="card shadow-2xl text-center">
            <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Password Reset Successful!
            </h2>
            <p className="text-gray-600 mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn-primary w-full"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!tokenValid) {
    return null
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/pexels-ethan-p-289678311-13177505.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-black bg-opacity-60" />
        
        {/* Back Button */}
        <div className="absolute top-6 left-6 z-20">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center text-white hover:text-gray-200 transition-colors duration-200 bg-black bg-opacity-30 hover:bg-opacity-50 rounded-lg px-3 py-2"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Login
          </button>
        </div>
        
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12 w-full h-full">
          <div className="text-center max-w-md mx-auto bg-black bg-opacity-30 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-20">
            <h1 className="mb-4 text-white drop-shadow-lg">
              <AxonallLogo size="text-4xl" className="text-white" />
            </h1>
            <p className="text-xl mb-6 text-white font-semibold drop-shadow-md">New Connected Dimension of Luxury Travel</p>
            <p className="text-lg text-white drop-shadow-md mb-4">
              Secure password reset for your travel advisor account
            </p>
            <p className="text-base text-white drop-shadow-md opacity-90">
              Choose a strong password to protect your account and client information
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Reset Password Form */}
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-10 w-32 h-32 bg-primary-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-blue-400 rounded-full blur-2xl"></div>
          <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-slate-400 rounded-full blur-xl"></div>
        </div>
        
        <div className="max-w-sm w-full space-y-6 relative z-10">
          {/* Header */}
          <div className="text-center">
            <div className="mb-6">
              <h1 className="mb-2">
                <AxonallLogo />
              </h1>
              <p className="text-gray-600 text-base font-medium">New Connected Dimension of Luxury Travel</p>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Reset Your Password
            </h2>
            <p className="text-gray-600 text-base">
              {userEmail && `For account: ${userEmail}`}
            </p>
          </div>

          {/* Reset Password Form */}
          <div className="card shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* New Password */}
              <div>
                <label htmlFor="password" className="form-label">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pl-10 pr-10"
                    placeholder="Enter new password"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters long'
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                        message: 'Password must contain uppercase, lowercase, number, and special character'
                      }
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="error-text">{errors.password.message}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="input-field pl-10 pr-10"
                    placeholder="Confirm new password"
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: value =>
                        value === password || 'Passwords do not match'
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="error-text">{errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">Password must contain:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    At least 8 characters
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    One uppercase letter
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    One lowercase letter
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    One number
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    One special character (@$!%*?&)
                  </li>
                </ul>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Resetting Password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center text-gray-500 text-sm mt-6">
            <p>© 2025 <AxonallLogo size="text-sm" />. All rights reserved.</p>
            <div className="flex justify-center space-x-4 mt-2 text-xs">
              <span className="text-gray-400">Privacy Policy</span>
              <span className="text-gray-400">Terms of Service</span>
              <span className="text-gray-400">Support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PasswordResetPage
